import { useState, useEffect } from 'react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { Link } from 'react-router-dom';
import SideBar from '../../components/global/SideBar';
import './index.css';
import { useCurrentUser, CancelClassModal, SuccessModal, StudioReviewModal, Loader, ErrorModal } from '@beholos/holoscommon';
import { fetchUserClasses, cancelUserClass } from '../../services/class.service';
import { fetchSchedules } from '../../services/schedule.service';
import { fetchHomePrograms } from '../../services/dashboard.service';
import type { UserClassInterface } from '../../types/userClass.interface';
import type { ClassInterface } from '../../types/class.interface';
import { ChevronRight, SearchIcon } from 'lucide-react';
import { Header } from './components/Header';
import { UserSchedule } from './components/UserSchedule';
import { RecommendedClasses } from './components/RecommendedClasses';
import { ProgramsGrid } from './components/ProgramsGrid';

export default function Dashboard() {
  usePageTitle('Inicio - Holos');
  const { user: currentUser, loading: loadingUser } = useCurrentUser();
  const [userClasses, setUserClasses] = useState<UserClassInterface[]>([]);
  const [loadingUserClasses, setLoadingUserClasses] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [selectedUserClass, setSelectedUserClass] = useState<UserClassInterface | null>(null);
  const [recommendedClasses, setRecommendedClasses] = useState<ClassInterface[]>([]);
  const [loadingRecommendedClasses, setLoadingRecommendedClasses] = useState(false);
  const [homePrograms, setHomePrograms] = useState<any[]>([]);
  const [loadingHomePrograms, setLoadingHomePrograms] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const loadUserClasses = async () => {
      try {
        setLoadingUserClasses(true);
        setError(null);
        const { classes } = await fetchUserClasses({ future: true, limit: 5 });
        setUserClasses(classes);
      } catch (err) {
        console.error('Error loading user classes:', err);
        setError('Error loading user classes');
      } finally {
        setLoadingUserClasses(false);
      }
    };

    const loadRecommendedClasses = async () => {
      try {
        setLoadingRecommendedClasses(true);
        const today = new Date();
        const dateString = today.toISOString().split('T')[0];
        const classes = await fetchSchedules({ date: dateString, page: 1, limit: 3 });
        setRecommendedClasses(classes);
      } catch (err) {
        console.error('Error loading recommended classes:', err);
      } finally {
        setLoadingRecommendedClasses(false);
      }
    };

    const loadHomePrograms = async () => {
      try {
        setLoadingHomePrograms(true);
        const programs = await fetchHomePrograms();
        setHomePrograms(programs);
      } catch (err) {
        console.error('Error loading home programs:', err);
      } finally {
        setLoadingHomePrograms(false);
      }
    };

    const checkForReviewModal = () => {
      const user = currentUser as any;
      if (user?.nextClassId && user?.nextClassTitle && user?.nextClassStudio) {
        setIsReviewModalOpen(true);
      }
    };

    if (currentUser?.id) {
      if (window.gtag) {
        window.gtag('set', { user_id: currentUser?.id });
      }
    }

    loadUserClasses();
    loadRecommendedClasses();
    loadHomePrograms();
    checkForReviewModal();
  }, [currentUser]);

  if (loadingUser || !currentUser || loadingUserClasses) {
    return (
      <div className="flex h-[calc(100vh-80px)] w-screen overflow-hidden">
        <div className="hidden md:block md:w-80 lg:w-96 h-full overflow-y-auto">
          <SideBar />
        </div>
        <div className="flex-1 h-screen overflow-y-auto flex items-center justify-center">
          <div className="text-center">
            <Loader />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-[calc(100vh-80px)] w-screen overflow-hidden">
        <div className="hidden md:block md:w-80 lg:w-96 h-full overflow-y-auto">
          <SideBar />
        </div>
        <div className="flex-1 h-full overflow-y-auto flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-holos hover:bg-holos-dark text-white font-bold py-2 px-4 rounded cursor-pointer"
            >
              Intenta de nuevo
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Handle cancel class confirmation
  const handleCancelClassConfirm = async (classId: string, _passes: number) => {
    try {
      const { status, message } = await cancelUserClass(classId);
      setIsCancelModalOpen(false);

      if (status !== 200) {
        throw new Error(message || 'No se pudo cancelar la clase. Por favor, intenta de nuevo.');
      }

      setIsSuccessModalOpen(true);
      // Refresh only user classes
      const { classes } = await fetchUserClasses({ future: true, limit: 5 });
      setUserClasses(classes);
    } catch (error) {
      console.error('Error cancelling class:', error);
      setErrorMessage(error instanceof Error ? error.message : "No se pudo cancelar la clase. Por favor, intenta de nuevo.");
    }
  };

  // Handle success modal close
  const handleSuccessModalClose = () => {
    setIsSuccessModalOpen(false);
  };

  // formatting moved into components

  return (
    <>
      <div className="flex h-[calc(100vh-80px)] w-screen overflow-hidden">
        <div className="hidden md:block md:w-80 lg:w-96 h-full overflow-y-auto">
          <SideBar />
        </div>

        <div className="flex-1 h-full overflow-y-auto relative">
          <div className="bg-gray-100">
            <Header currentUser={currentUser} showBanner={showBanner} onCloseBanner={() => setShowBanner(false)} />
            <div className="dashboard-next-classes pb-5 pt-3 mx-auto">
              <div className="inner max-w-5xl mx-auto px-4">
                <div className="mt-6 md:mt-15 mb-3 py-4 rounded overflow-hidden">
                  <div className="flex items-center justify-between w-full px-4 md:px-6 py-4 bg-white rounded-lg shadow-sm">
                    <p className="text-lg font-black font-bold mb-0 font-juana">Tu programación</p>
                    <div className="flex items-center">
                      <Link
                        to="/mis-clases"
                        className="text-holos-dark font-medium text-xs whitespace-nowrap hover:text-holos flex justify-center items-center"
                      >
                        Ver agenda completa
                        <ChevronRight className="inline-block" width={15} />
                      </Link>
                    </div>
                  </div>
                  <div className="flex border-t border-gray-200 w-full mx-0 mt-2 md:mt-4 bg-white rounded-lg">
                    <div className="w-full md:w-1/2 text-sm text-center border-r border-gray-200 py-3 font-bold font-juana">
                      TUS PRÓXIMAS CLASES
                    </div>
                    <div className="w-1/2 hidden md:flex py-3">&nbsp;</div>
                  </div>
                </div>

                <div className="flex flex-wrap px-0 md:px-3">
                  <UserSchedule
                    userClasses={userClasses}
                    onCancel={(userClass) => { setSelectedUserClass(userClass); setIsCancelModalOpen(true); }}
                  />

                  <div className="w-full lg:w-1/2 mb-2 pr-0 pl-0 lg:pl-2">
                    <div className="bg-white rounded-lg shadow-sm flex items-center justify-center py-6 h-full">
                      <div className="w-full px-4">
                        <div className="text-base font-black text-center mb-6">
                          Busca tu próxima clase aquí.
                        </div>
                        <div className="mb-6 relative">
                          <input
                            type="text"
                            placeholder="Buscar clases, entrenadores, estudios..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full py-3 pl-12 pr-4 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-300 bg-white"
                            style={{ paddingLeft: '48px' }}
                          />
                          <div className="absolute left-0 top-0 h-full w-12 flex items-center justify-center">
                            <SearchIcon className="text-lg text-gray-400" />
                          </div>
                        </div>
                        <div className="flex justify-center items-center gap-4">
                          <Link
                            to={`/clases-online?query=${encodeURIComponent(searchQuery)}`}
                            className="uppercase text-sm text-black bg-white border-2 border-black hover:bg-holos-light hover:text-white px-8 py-3 rounded-full font-medium transition-colors flex-1 text-center"
                          >
                            VIDEOS
                          </Link>
                          <Link
                            to={`/clases?query=${encodeURIComponent(searchQuery)}`}
                            className="uppercase text-sm text-black bg-white border-2 border-black hover:bg-holos-light hover:text-white px-8 py-3 rounded-full font-medium transition-colors flex-1 text-center"
                          >
                            CLASES
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="dashboard-inner w-full bg-gray-100 mx-auto">
              <div className="mb-3 py-4 rounded overflow-hidden max-w-5xl mx-auto px-4">
                <div className="flex items-center justify-between w-full px-4 md:px-6 py-4 bg-white rounded-lg shadow-sm">
                  <p className="text-lg font-black font-bold mb-0 font-juana">Videos on-demand por categoría</p>
                  <div className="flex items-center">
                    <Link
                      to="/clases-online"
                      className="text-holos-dark font-medium text-xs whitespace-nowrap hover:text-holos flex justify-center items-center"
                    >
                      Ver todo
                      <ChevronRight className="inline-block" width={15} />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="hidden lg:flex justify-around mb-3 px-0 md:px-3 mb-5 max-w-5xl mx-auto">
              {[
                { name: 'prosper', img: 'https://image.beholos.com/fit-in/400x450/img/fitness-pass-v2/corporate/dashboard/categoryprosper001.jpg', icon: 'https://cache.beholos.com/img/fitness-pass-v2/corporate/saas-cat-prosper.svg' },
                { name: 'move', img: 'https://image.beholos.com/fit-in/400x450/img/fitness-pass-v2/corporate/dashboard/categorymove001.jpg', icon: 'https://cache.beholos.com/img/fitness-pass-v2/corporate/saas-cat-move.svg' },
                { name: 'eat', img: 'https://image.beholos.com/fit-in/400x450/img/fitness-pass-v2/corporate/dashboard/categoryeat001.jpg', icon: 'https://cache.beholos.com/img/fitness-pass-v2/corporate/saas-cat-eat.svg' },
                { name: 'play', img: 'https://image.beholos.com/fit-in/400x450/img/fitness-pass-v2/corporate/dashboard/categoryplay001.jpg', icon: 'https://cache.beholos.com/img/fitness-pass-v2/corporate/saas-cat-play.svg' },
                { name: 'relax', img: 'https://image.beholos.com/fit-in/400x450/img/fitness-pass-v2/corporate/dashboard/categoryrelax001.jpg', icon: 'https://cache.beholos.com/img/fitness-pass-v2/corporate/saas-cat-relax.svg' }
              ].map((category, idx) => (
                <div key={idx} className="flex-1 overflow-hidden mb-0 pr-2" style={{ height: '200px' }}>
                  <Link
                    to={`/clases-online?saasCategory=${category.name}`}
                    className="h-full flex relative items-end justify-center bg-cover bg-no-repeat rounded-lg"
                    style={{ backgroundImage: `url("${category.img}")` }}
                  >
                    {(() => {
                      const bgMap: Record<string, string> = {
                        prosper: 'bg-prosper',
                        move: 'bg-move',
                        eat: 'bg-eat',
                        play: 'bg-play',
                        relax: 'bg-relax',
                      };
                      const bgClass = bgMap[category.name] || 'bg-white';
                      return (
                        <div className={`inline-flex items-center gap-2 px-4 py-1 mb-2 rounded-full shadow-sm absolute bottom-3 left-1/2 -translate-x-1/2 transform ${bgClass} text-black`}>
                          <div className="text-sm font-black uppercase">{category.name}</div>
                          <img src={category.icon} alt={category.name} className="w-5 h-5 max-w-none flex-shrink-0" />
                        </div>
                      );
                    })()}
                  </Link>
                </div>
              ))}
            </div>

            <div className="flex lg:hidden justify-around mb-3 px-0 mb-5 pb-5 w-full max-w-5xl mx-auto">
              <div className="flex overflow-x-auto space-x-4 px-4">
                {[
                  { name: 'prosper', img: 'https://image.beholos.com/fit-in/400x450/img/fitness-pass-v2/corporate/dashboard/categoryprosper001.jpg', icon: 'https://cache.beholos.com/img/fitness-pass-v2/corporate/saas-cat-prosper.svg' },
                  { name: 'move', img: 'https://image.beholos.com/fit-in/400x450/img/fitness-pass-v2/corporate/dashboard/categorymove001.jpg', icon: 'https://cache.beholos.com/img/fitness-pass-v2/corporate/saas-cat-move.svg' },
                  { name: 'eat', img: 'https://image.beholos.com/fit-in/400x450/img/fitness-pass-v2/corporate/dashboard/categoryeat001.jpg', icon: 'https://cache.beholos.com/img/fitness-pass-v2/corporate/saas-cat-eat.svg' }
                ].map((category, idx) => (
                  <div key={idx} className="flex-shrink-0 w-64 overflow-hidden" style={{ height: '200px' }}>
                    <Link
                      to={`/clases-online?saasCategory=${category.name}`}
                      className="h-full flex relative items-end justify-center bg-cover bg-no-repeat rounded-lg"
                      style={{ backgroundImage: `url("${category.img}")` }}
                    >
                      {(() => {
                        const bgMap: Record<string, string> = {
                          prosper: 'bg-prosper',
                          move: 'bg-move',
                          eat: 'bg-eat',
                          play: 'bg-play',
                          relax: 'bg-relax',
                        };
                        const bgClass = bgMap[category.name] || 'bg-white';
                        return (
                          <div className={`inline-flex items-center gap-2 px-4 py-1 mb-2 rounded-full shadow-sm absolute bottom-3 left-1/2 -translate-x-1/2 transform ${bgClass} text-black`}>
                            <div className="text-sm font-black uppercase">{category.name}</div>
                            <img src={category.icon} alt={category.name} className="w-5 h-5 max-w-none flex-shrink-0" />
                          </div>
                        );
                      })()}
                    </Link>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-3 py-4 rounded overflow-hidden max-w-5xl mx-auto px-4">
              <div className="flex items-center justify-between w-full px-4 md:px-6 py-4 bg-white rounded-lg shadow-sm">
                <p className="text-lg font-black font-bold mb-0 font-juana">Sesiones Individuales Online</p>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row dashboard-features px-4 md:px-3 mb-5 max-w-5xl mx-auto gap-4">
              <div className="w-full lg:w-1/2 overflow-hidden h-full pr-0 lg:pr-2">
                <div className="h-1/2 pb-2">
                  <Link
                    to="/psicologia"
                    className="block relative rounded-lg h-40 bg-cover bg-top"
                    style={{ backgroundImage: `url("https://image.beholos.com/fit-in/1000x500/img/holos/dashboard/psychologist.jpg")` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent rounded-lg"></div>
                    <div className="absolute bottom-4 left-4 text-white">
                      <h2 className="text-lg uppercase font-bold italic">PSICOLOGÍA</h2>
                      <div className="text-xs">Terapia es fundamental para tu crecimiento personal. Elimina obstáculos que te impiden lograr tus objetivos.</div>
                    </div>
                  </Link>
                </div>
                <div className="h-1/2 pt-2">
                  <Link
                    to="/finanzas/"
                    className="block relative rounded-lg h-40 bg-cover bg-top"
                    style={{ backgroundImage: `url("https://image.beholos.com/fit-in/1000x500/img/holos/dashboard/gonzalofront.jpg")` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent rounded-lg"></div>
                    <div className="absolute bottom-4 left-4 text-white">
                      <h2 className="text-lg uppercase font-bold italic">COACH FINANCIERO</h2>
                      <div className="text-xs">Conoce las herramientas disponibles para tu futuro con una asesoría sencilla y fácil de entender.</div>
                    </div>
                  </Link>
                </div>
              </div>
              <div className="w-full lg:w-1/2 overflow-hidden h-full pl-0 lg:pl-2">
                <div className="h-1/2 pb-2">
                  <Link
                    to="/health-coach"
                    className="block relative rounded-lg h-40 bg-cover bg-top"
                    style={{ backgroundImage: `url("https://image.beholos.com/fit-in/1000x500/img/holos/dashboard/healthcoach.jpg")` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent rounded-lg"></div>
                    <div className="absolute bottom-4 left-4 text-white">
                      <h2 className="text-lg uppercase font-bold italic">HEALTH COACH</h2>
                      <div className="text-xs">Un espacio seguro para explorar tu bienestar de la mano de un coach que esté de tu lado.</div>
                    </div>
                  </Link>
                </div>
                <div className="h-1/2 pt-2">
                  <Link
                    to="/nutricion"
                    className="block relative rounded-lg h-40 bg-cover bg-top"
                    style={{ backgroundImage: `url("https://image.beholos.com/fit-in/1000x500/img/holos/dashboard/caropa2.jpg")` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent rounded-lg"></div>
                    <div className="absolute bottom-4 left-4 text-white">
                      <h2 className="text-lg uppercase font-bold italic">NUTRICIONISTA</h2>
                      <div className="text-xs">Elige según su especialidad y recibe un plan mensual de alimentación.</div>
                    </div>
                  </Link>
                </div>
              </div>
            </div>

            <div className="mb-5 py-4 overflow-hidden max-w-5xl mx-auto px-4">
              <div className="flex items-center justify-between w-full px-4 md:px-6 py-4 bg-white rounded-lg shadow-sm">
                <p className="text-lg font-black font-bold mb-0 font-juana">Agenda clases online o presencial</p>
                <div className="flex items-center">
                  <Link
                    to="/clases"
                    className="text-holos-dark font-medium text-xs whitespace-nowrap hover:text-holos flex justify-center items-center"
                  >
                    Ver todo
                    <ChevronRight className="inline-block" width={15} />
                  </Link>
                </div>
              </div>

              <div className="mt-4 border-t border-b border-gray-200 py-3 hidden md:flex items-center justify-around bg-white rounded-lg shadow-sm">
                {[
                  { name: 'Meditación', category: 'meditación', position: '0% 0%' },
                  { name: 'Baile', category: 'baile', position: '0% 20%' },
                  { name: 'Strength', category: 'musculación', position: '0% 40%' },
                  { name: 'Cardio', category: 'cardio', position: '0% 60%' },
                  { name: 'Functional', category: 'functional training', position: '0% 80%' },
                  { name: 'Yoga', category: 'yoga', position: '0% 100%' }
                ].map((cat, idx) => (
                  <Link
                    key={idx}
                    to={`/clases?category=${cat.category}`}
                    className="flex flex-col justify-center items-center text-center hover:text-holos-dark"
                  >
                    <div
                      className="w-12 h-12 mb-2 bg-cover"
                      style={{
                        backgroundImage: 'url("https://cache.beholos.com/img/fitness-pass-v2/corporate/dashboard/corp-dashboard-class-cat-sprite-v2.png")',
                        backgroundPosition: cat.position,
                        backgroundSize: '100% 600%'
                      }}
                    ></div>
                    <div className="text-gray-600 text-xs py-2">{cat.name}</div>
                  </Link>
                ))}
              </div>

              <RecommendedClasses classes={recommendedClasses} isLoading={loadingRecommendedClasses} />
            </div>

            <div className="mb-3 py-4 rounded overflow-hidden max-w-5xl mx-auto px-4">
              <div className="flex items-center justify-between w-full px-4 md:px-6 py-4 bg-white rounded-lg shadow-sm">
                <p className="text-lg font-black font-bold mb-0 font-juana">Programas y Retos</p>
                <div className="flex items-center">
                  <Link
                    to="/programas"
                    className="text-holos-dark font-medium text-xs whitespace-nowrap hover:text-holos flex justify-center items-center"
                  >
                    Ver todo
                    <ChevronRight className="inline-block" width={15} />
                  </Link>
                </div>
              </div>
            </div>

            <div className="mb-5 px-0 md:px-3 max-w-5xl mx-auto">
              <ProgramsGrid programs={homePrograms} isLoading={loadingHomePrograms} />
            </div>
          </div>
        </div>
      </div>
      {/* Cancel Class Modal */}
      {isCancelModalOpen && selectedUserClass && (
        <CancelClassModal
          isOpen={isCancelModalOpen}
          onClose={() => setIsCancelModalOpen(false)}
          cancelClassData={{
            classId: selectedUserClass._id,
            passes: selectedUserClass.class?.passes || 0,
            classStartTime: selectedUserClass.startTime,
            classEndTime: selectedUserClass.endTime,
            cancelTimeRestriction: selectedUserClass.class?.cancelTimeRestriction || 0,
            isVideo: false,
            studioName: selectedUserClass.class?.studio?.name || '',
            className: selectedUserClass.class?.title || ''
          }}
          cancelClassConfirm={handleCancelClassConfirm}
        />
      )}

      {/* Success Modal */}
      <SuccessModal
        isOpen={isSuccessModalOpen}
        onClose={handleSuccessModalClose}
        title="¡Clase Cancelada!"
        message="Tu clase ha sido cancelada satisfactoriamente. Puedes revisar tu agenda para confirmar los cambios."
        buttonText="Aceptar"
      />

      {/* Error Modal */}
      <ErrorModal
        isOpen={!!errorMessage}
        onClose={() => setErrorMessage(null)}
        title="¡Error!"
        message={errorMessage || "Algo salió mal, escríbenos a contacto@beholos.com."}
      />

      {/* Studio Review Modal */}
      {currentUser && (currentUser as any).nextClassId && (
        <StudioReviewModal
          isOpen={isReviewModalOpen}
          onClose={() => setIsReviewModalOpen(false)}
          userClass={{
            _id: (currentUser as any).nextClassId,
            class: {
              _id: (currentUser as any).nextClassId,
              title: (currentUser as any).nextClassTitle,
              studio: {
                name: (currentUser as any).nextClassStudio,
                slug: (currentUser as any).nextClassLink?.split('/')[2] || ''
              },
              liveStreamClass: (currentUser as any).nextClassIsVideo || false,
              liveStreamUrl: ''
            },
            startTime: (currentUser as any).nextClassDate,
            endTime: (currentUser as any).nextClassDate
          }}
          isVideo={(currentUser as any).nextClassIsVideo || false}
          firstname={currentUser?.firstname || ''}
          link={(currentUser as any).nextClassLink || ''}
        />
      )}
    </>
  );
}
